// ══════════════════════════════════════════════════════════════════════════════
// AdminPage.js — Internal admin dashboard (Gita + Jyotish)
// Access: /?admin=1 — gated by ADMIN_SECRET, entered once and kept in sessionStorage
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");
const SECRET_KEY = "vedsanga_admin_secret";

const C = {
  bg: "#0D0F14", panel: "#151822", panel2: "#1B1F2C",
  accent: "#E8832A", text: "#EDEBE6", dim: "#8A8F9E",
  border: "rgba(255,255,255,0.08)",
  good: "#6ee7b7", bad: "#fca5a5",
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "users", label: "Users" },
  { id: "jyotish", label: "Jyotish Q&A" },
  { id: "stories", label: "Stories" },
  { id: "reports", label: "Report Tools" },
];

async function adminFetch(path, secret, opts = {}) {
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${API}${path}${sep}secret=${encodeURIComponent(secret)}`, opts);
  if (res.status === 403) throw new Error("forbidden");
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.json();
}

export default function AdminPage() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem(SECRET_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [tab, setTab] = useState("overview");

  const tryAuth = useCallback((s) => {
    setAuthBusy(true);
    setAuthErr("");
    adminFetch("/admin/stats", s)
      .then(() => {
        sessionStorage.setItem(SECRET_KEY, s);
        setSecret(s);
        setAuthed(true);
      })
      .catch((e) => {
        setAuthErr(e.message === "forbidden" ? "Wrong secret." : "Couldn't reach backend.");
      })
      .finally(() => setAuthBusy(false));
  }, []);

  useEffect(() => {
    if (secret) tryAuth(secret);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div style={s.loginWrap}>
        <div style={s.loginCard}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>🔐</div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>VedSanga Admin</div>
          <input
            type="password"
            placeholder="Admin secret"
            defaultValue={secret}
            autoFocus
            style={s.input}
            onKeyDown={(e) => { if (e.key === "Enter") tryAuth(e.target.value.trim()); }}
            id="admin-secret-input"
          />
          <button
            style={s.btnPrimary}
            disabled={authBusy}
            onClick={() => tryAuth(document.getElementById("admin-secret-input").value.trim())}
          >
            {authBusy ? "Checking…" : "Enter"}
          </button>
          {authErr && <div style={{ color: C.bad, fontSize: 13, marginTop: 10 }}>{authErr}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>VedSanga Admin</div>
        <button
          style={s.btnGhost}
          onClick={() => { sessionStorage.removeItem(SECRET_KEY); setAuthed(false); setSecret(""); }}
        >
          Log out
        </button>
      </div>
      <div style={s.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{ ...s.tabBtn, ...(tab === t.id ? s.tabBtnActive : {}) }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={s.body}>
        {tab === "overview" && <Overview secret={secret} />}
        {tab === "users" && <Users secret={secret} />}
        {tab === "jyotish" && <JyotishConversations secret={secret} />}
        {tab === "stories" && <Stories secret={secret} />}
        {tab === "reports" && <ReportTools secret={secret} />}
      </div>
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────

function Overview({ secret }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminFetch("/admin/stats", secret).then(setStats).catch(() => setErr("Failed to load stats."));
  }, [secret]);

  if (err) return <div style={{ color: C.bad }}>{err}</div>;
  if (!stats) return <div style={{ color: C.dim }}>Loading…</div>;

  const cards = [
    { label: "Users with birth data", value: stats.total_users },
    { label: "Active Jyotish subscriptions", value: stats.active_subscriptions },
    { label: "Gita chat sessions", value: stats.total_chat_sessions },
    { label: "Jyotish Q&A logged", value: stats.total_jyotish_qs },
    { label: "Shared stories", value: stats.total_stories },
  ];

  return (
    <div style={s.grid}>
      {cards.map((c) => (
        <div key={c.label} style={s.statCard}>
          <div style={{ fontSize: 30, fontWeight: 700, color: C.accent }}>{c.value}</div>
          <div style={{ color: C.dim, fontSize: 13, marginTop: 4 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Users ───────────────────────────────────────────────────────────────────

function Users({ secret }) {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    adminFetch("/admin/users?limit=200", secret).then((d) => setUsers(d.users)).catch(() => setErr("Failed to load users."));
  }, [secret]);

  useEffect(load, [load]);

  const grant = (userId) => {
    setBusyId(userId);
    fetch(`${API}/jyotish/subscription/grant?secret=${encodeURIComponent(secret)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, plan: "annual", expires_at: null, granted_by: "admin_ui" }),
    })
      .then(() => load())
      .finally(() => setBusyId(null));
  };

  const revoke = (userId) => {
    setBusyId(userId);
    fetch(`${API}/jyotish/subscription/${encodeURIComponent(userId)}?secret=${encodeURIComponent(secret)}`, { method: "DELETE" })
      .then(() => load())
      .finally(() => setBusyId(null));
  };

  if (err) return <div style={{ color: C.bad }}>{err}</div>;
  if (!users) return <div style={{ color: C.dim }}>Loading…</div>;
  if (!users.length) return <div style={{ color: C.dim }}>No users with saved birth data yet.</div>;

  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>User ID</th>
            <th style={s.th}>DOB</th>
            <th style={s.th}>City</th>
            <th style={s.th}>Saved</th>
            <th style={s.th}>Jyotish Plan</th>
            <th style={s.th}>Expires</th>
            <th style={s.th}></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id}>
              <td style={s.td}><code style={{ fontSize: 12 }}>{u.user_id}</code></td>
              <td style={s.td}>{u.dob || "—"}</td>
              <td style={s.td}>{u.birth_city || "—"}</td>
              <td style={s.td}>{fmtDate(u.saved_at)}</td>
              <td style={s.td}>
                {u.subscribed
                  ? <span style={{ color: C.good }}>{u.plan}</span>
                  : <span style={{ color: C.dim }}>none</span>}
              </td>
              <td style={s.td}>{u.expires_at || (u.subscribed ? "lifetime" : "—")}</td>
              <td style={s.td}>
                {u.subscribed ? (
                  <button style={s.btnSmallBad} disabled={busyId === u.user_id} onClick={() => revoke(u.user_id)}>Revoke</button>
                ) : (
                  <button style={s.btnSmallGood} disabled={busyId === u.user_id} onClick={() => grant(u.user_id)}>Grant premium</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Jyotish conversations ────────────────────────────────────────────────────

function JyotishConversations({ secret }) {
  const [rows, setRows] = useState(null);
  const [userId, setUserId] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    const q = userId.trim() ? `&user_id=${encodeURIComponent(userId.trim())}` : "";
    adminFetch(`/admin/jyotish/conversations?limit=50${q}`, secret).then(setRows).catch(() => setErr("Failed to load."));
  }, [secret, userId]);

  useEffect(load, [load]);

  if (err) return <div style={{ color: C.bad }}>{err}</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input
          style={{ ...s.input, marginBottom: 0, maxWidth: 280 }}
          placeholder="Filter by user_id (optional)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <button style={s.btnGhost} onClick={load}>Search</button>
      </div>
      {!rows ? (
        <div style={{ color: C.dim }}>Loading…</div>
      ) : !rows.length ? (
        <div style={{ color: C.dim }}>No conversations logged yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((r) => (
            <div key={r.id} style={s.qaCard}>
              <div style={{ color: C.dim, fontSize: 12, marginBottom: 6 }}>
                {fmtDate(r.created_at)} · <code>{r.user_id}</code>
                {r.maha_dasha && ` · Dasha: ${r.maha_dasha}${r.antardasha ? " / " + r.antardasha : ""}`}
              </div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.question}</div>
              <div style={{ color: C.text, opacity: 0.85, fontSize: 14 }}>{r.reply}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stories moderation ────────────────────────────────────────────────────────

function Stories({ secret }) {
  const [stories, setStories] = useState(null);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/stories?limit=50`).then((r) => r.json()).then((d) => setStories(d.stories || [])).catch(() => setErr("Failed to load stories."));
  }, []);

  useEffect(load, [load]);

  const del = (id) => {
    if (!window.confirm("Delete this story? This can't be undone.")) return;
    setBusyId(id);
    fetch(`${API}/admin/stories/${id}?secret=${encodeURIComponent(secret)}`, { method: "DELETE" })
      .then(() => load())
      .finally(() => setBusyId(null));
  };

  if (err) return <div style={{ color: C.bad }}>{err}</div>;
  if (!stories) return <div style={{ color: C.dim }}>Loading…</div>;
  if (!stories.length) return <div style={{ color: C.dim }}>No stories shared yet.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {stories.map((st) => (
        <div key={st.id} style={s.qaCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{st.journey_icon} {st.journey_title}</div>
              <div style={{ color: C.dim, fontSize: 12, margin: "4px 0" }}>
                {fmtDate(st.shared_at)} · {st.days_done} days · avg {st.avg_score} · {st.likes} likes
              </div>
              {st.ai_reflection && <div style={{ fontSize: 14, opacity: 0.85 }}>{st.ai_reflection}</div>}
            </div>
            <button style={s.btnSmallBad} disabled={busyId === st.id} onClick={() => del(st.id)}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Report test tools ────────────────────────────────────────────────────────

function ReportTools({ secret }) {
  const [phone, setPhone] = useState("");
  const [matchId, setMatchId] = useState("");
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState([]);

  const run = (label, path) => {
    setBusy(label);
    adminFetch(path, secret)
      .then((d) => setLog((l) => [{ label, ok: true, ts: Date.now(), data: JSON.stringify(d).slice(0, 200) }, ...l]))
      .catch((e) => setLog((l) => [{ label, ok: false, ts: Date.now(), data: e.message }, ...l]))
      .finally(() => setBusy(""));
  };

  return (
    <div>
      <div style={s.qaCard}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Send report to phone (no payment)</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input style={{ ...s.input, marginBottom: 0 }} placeholder="Phone (e.g. 91987...)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={s.btnGhost} disabled={!phone || busy} onClick={() => run("Shani test", `/admin/shani/test?phone=${encodeURIComponent(phone)}`)}>Trigger Shani report</button>
          <button style={s.btnGhost} disabled={!phone || busy} onClick={() => run("Kundali test", `/admin/kundali/test?phone=${encodeURIComponent(phone)}`)}>Trigger Kundali report</button>
        </div>
      </div>

      <div style={{ ...s.qaCard, marginTop: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Resend Milan match report</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...s.input, marginBottom: 0, maxWidth: 160 }} placeholder="match_id" value={matchId} onChange={(e) => setMatchId(e.target.value)} />
          <button style={s.btnGhost} disabled={!matchId || busy} onClick={() => run("Milan test", `/admin/milan/test?match_id=${encodeURIComponent(matchId)}`)}>Trigger Milan report</button>
        </div>
      </div>

      {log.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {log.map((l) => (
            <div key={l.ts} style={{ fontSize: 12, color: l.ok ? C.good : C.bad, marginBottom: 4 }}>
              {new Date(l.ts).toLocaleTimeString()} · {l.label} · {l.ok ? "OK" : "FAILED"} · {l.data}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page: { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "system-ui, sans-serif", padding: "0 0 60px" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 24px", borderBottom: `1px solid ${C.border}`,
  },
  tabs: { display: "flex", gap: 4, padding: "12px 24px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" },
  tabBtn: {
    background: "transparent", border: "none", color: C.dim, padding: "8px 14px",
    borderRadius: 8, cursor: "pointer", fontSize: 14,
  },
  tabBtnActive: { background: C.panel2, color: C.text, fontWeight: 600 },
  body: { padding: 24, maxWidth: 1100, margin: "0 auto" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 },
  statCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 16px" },
  loginWrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, color: C.text, fontFamily: "system-ui, sans-serif" },
  loginCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 300, textAlign: "center" },
  input: {
    width: "100%", boxSizing: "border-box", background: C.panel2, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 14, marginBottom: 12, outline: "none",
  },
  btnPrimary: {
    width: "100%", background: C.accent, border: "none", borderRadius: 8, padding: "10px 12px",
    color: "#1a1200", fontWeight: 600, cursor: "pointer", fontSize: 14,
  },
  btnGhost: {
    background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 14px",
    color: C.text, cursor: "pointer", fontSize: 13,
  },
  btnSmallGood: {
    background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 6,
    padding: "5px 10px", color: C.good, cursor: "pointer", fontSize: 12,
  },
  btnSmallBad: {
    background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6,
    padding: "5px 10px", color: C.bad, cursor: "pointer", fontSize: 12,
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${C.border}`, color: C.dim, fontWeight: 600 },
  td: { padding: "8px 10px", borderBottom: `1px solid ${C.border}` },
  qaCard: { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" },
};
